import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhysicalAssetType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';
import {
  AssignBeneficiaryDto,
  PhysicalAsset
} from 'service/dto/physical-asset.dto';

export const useCreatePhysicalAsset = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (physicalAssetType: PhysicalAssetType): Promise<void> => {
      return services.physicalAssetService.createPhysicalAsset(
        physicalAssetType
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-physical']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetPhysicalAssetList = () =>
  useQuery(
    ['physicals'],
    () => services.physicalAssetService.getPhysicalAssets(),
    {
      refetchOnWindowFocus: false
    }
  );

export const useDeletePhysicalAsset = () =>
  useMutation(
    (assetId: number) =>
      services.physicalAssetService.deletePhysicalAsset(assetId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignPhysicalAssetBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.physicalAssetService.assignBeneficiary(
        assignBeneficiaryDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-physical-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetPhysicalAssetDetails = ({
  assetId
}: {
  assetId: number;
}) => {
  return useQuery<PhysicalAsset, AxiosError<Error>>(
    ['get_physical', assetId],
    () => services.physicalAssetService.getPhysicalAsset(assetId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!assetId
    }
  );
};

export const useUnAssignPhysicalAssetBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.physicalAssetService.unAssignPhysicalAssetBeneficiary(
        allocationId
      ),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const usePhysicalAssetAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.physicalAssetService.editBulkPhysicalAssetBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['physical-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemovePhysicalTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.physicalAssetService.removePhysicalTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignPhysicalTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.physicalAssetService.assignPhysicalTrustee(
        assetId,
        trusteeId,
        note
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-physical-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
